import type { DataFreshness, PosthogFeatureFlag } from "@unbox/api";
import { useEffect, useState } from "react";
import { client, useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useFocusTrap } from "@/pages/ask/hooks/useFocusTrap";
import DateRangeCalendar, { type PresetLabel } from "./DateRangeCalendar";

// ---------------------------------------------------------------------------
// Template catalogue (mirrors the YAML files in packages/api/src/resources/ask/reports/templates/)
// ---------------------------------------------------------------------------
export interface TemplateMeta {
  id: string;
  title: string;
  description: string;
  icon: "shield" | "sparkle" | "users" | "trending" | "calendar";
  requiresDateRange?: true;
  /** Restricts the date range picker to a window of exactly this many days. */
  windowDays?: number;
  featureFlag?: PosthogFeatureFlag;
  /** Workspace templates cover every brand in the workspace and only appear on workspace routes. */
  scope?: "workspace";
}

export type ReportRequest = { template: TemplateMeta; additionalInputs?: Record<string, string> };

export const REPORT_TEMPLATES: TemplateMeta[] = [
  {
    id: "business-review",
    title: "Business Review",
    description: "Period GMV, conversion funnel, top creators, top content, and programme health vs prior period.",
    icon: "calendar",
    requiresDateRange: true,
  },
  {
    id: "brand-safety-risk-profile",
    title: "Safety Risk Profile",
    description: "Risk types, creator-contact loop, and compliance funnel over a selected window.",
    icon: "shield",
    requiresDateRange: true,
  },
  {
    id: "weekly-safety-report",
    title: "Weekly Safety Report",
    description:
      "Week-on-week safety progress: review coverage by severity, an eight-week trend, review and enforcement activity, and the risk types behind them.",
    icon: "shield",
    requiresDateRange: true,
  },
  {
    id: "starter-sku-recommendation",
    title: "Starter SKU Recommendation",
    description: "Which product gives a new affiliate creator the best shot at first-video GMV.",
    icon: "sparkle",
  },
  {
    id: "sample-efficiency",
    title: "Sample Efficiency",
    description:
      "Samples sent, content produced per sample, and the orders and GMV they drive, split by open/target and free/refundable.",
    icon: "trending",
    requiresDateRange: true,
  },
  {
    id: "creator-success-profile",
    title: "Creator Success Profile",
    description:
      "Which creator traits and content signals (archetypes, tactics, hooks) correlate with high GMV for this brand.",
    icon: "users",
    requiresDateRange: true,
  },
  {
    id: "wbr-brand-extract",
    title: "Workspace WBR Extract",
    description:
      "One month-grid WBR data extract per brand in the workspace: weekly values, WoW changes, and MTD for every WBR metric, top 5 SKUs, and a top-10 creator snapshot for the latest week. Current month by default.",
    icon: "calendar",
    featureFlag: "F-workspace-reports",
    scope: "workspace",
  },
  {
    id: "grant-wbr",
    title: "Grant WBR",
    description:
      "Three-week WBR data extract for this brand: a 7-day window and the two weeks before it, with WoW changes for every WBR metric, top 5 SKUs, and a top-10 creator snapshot.",
    icon: "calendar",
    requiresDateRange: true,
    windowDays: 7,
    featureFlag: "F-workspace-reports",
  },
  {
    id: "wbr-affiliate-pack",
    title: "Affiliate Data Pack",
    description:
      "Three-table affiliate extract: a month grid of WBR metrics with WoW and MTD, product-grain actuals with cumulative samples, and a creator snapshot for the latest week. Current month by default.",
    icon: "calendar",
    featureFlag: "F-affiliate-data-pack",
  },
];

export function useVisibleReportTemplates(): TemplateMeta[] {
  // Every flag a template gates on is read unconditionally here, because hooks
  // cannot run inside the filter. Add a line when a template introduces a new
  // flag: a flag missing from this map reads as undefined, so that template
  // stays hidden rather than leaking into every account.
  const enabledByFlag: Partial<Record<PosthogFeatureFlag, boolean>> = {
    "F-workspace-reports": useFeatureFlag("F-workspace-reports"),
    "F-affiliate-data-pack": useFeatureFlag("F-affiliate-data-pack"),
  };
  const { workspaceId } = useScopedMerchantClient();
  return REPORT_TEMPLATES.filter((t) => {
    if (t.featureFlag && !enabledByFlag[t.featureFlag]) return false;
    if (t.scope === "workspace" && !workspaceId) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// Templates whose queries are bounded on video_day_metrics.day (≈2-day warehouse
// lag) use vdm_latest as the picker cap. The rest are bounded on
// videos.publish_date (no lag) and use publish_date_latest. Keeping this map
// here rather than on each template avoids a YAML-side concept the picker
// would otherwise need to reach into.
const VDM_BOUNDED_TEMPLATES = new Set(["business-review", "grant-wbr"]);

// Weekly templates report the Monday to Sunday week containing the picked end
// date, so the picker opens on the last complete week.
const DEFAULT_PRESET_BY_TEMPLATE: Partial<Record<string, PresetLabel>> = { "weekly-safety-report": "Last week" };

// Date range picker — centered modal overlay shown when a date-range template
// is clicked. Fetches per-merchant warehouse freshness on open and caps the
// calendar at the latest available date, so users never pick a date with no
// data and the "delta from today to latest data" is visible up-front.
// ---------------------------------------------------------------------------
export function DateRangePicker({
  templateId,
  templateTitle,
  brandName,
  onConfirm,
  onCancel,
}: {
  templateId: string;
  templateTitle: string;
  brandName: string;
  onConfirm: (startDate: string, endDate: string) => void;
  onCancel: () => void;
}) {
  const scope = useScopedMerchantClient();
  const [freshness, setFreshness] = useState<DataFreshness | null>(null);
  const dialogRef = useFocusTrap(true);

  useEffect(() => {
    let cancelled = false;
    const noData: DataFreshness = { merchant_id: scope.merchantId, vdm_latest: null, publish_date_latest: null };
    const fetchFreshness = async (): Promise<DataFreshness> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].ask["data-freshness"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].ask["data-freshness"].$get({ param: scope.param });
      return res.ok ? await res.json() : noData;
    };
    fetchFreshness()
      .then((d) => {
        if (!cancelled) setFreshness(d);
      })
      .catch(() => {
        if (!cancelled) setFreshness(noData);
      });
    return () => {
      cancelled = true;
    };
  }, [scope.merchantId, scope.workspaceId, scope.param]);

  // Escape closes the modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const maxDate = freshness
    ? ((VDM_BOUNDED_TEMPLATES.has(templateId) ? freshness.vdm_latest : freshness.publish_date_latest) ?? undefined)
    : undefined;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close mirrors the dialog's Escape handler
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(17, 17, 17, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stops backdrop close when clicking inside the dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Select date range for ${templateTitle}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "100%", outline: "none" }}
      >
        {freshness === null ? (
          // Brief skeleton — typical fetch is ~100-200ms.
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--color-border-secondary)",
              borderRadius: 14,
              padding: "28px 32px",
              fontSize: 13,
              color: "var(--color-text-tertiary)",
            }}
          >
            Loading warehouse freshness…
          </div>
        ) : (
          <DateRangeCalendar
            title={`${templateTitle} · ${brandName}`}
            maxDate={maxDate}
            windowDays={REPORT_TEMPLATES.find((t) => t.id === templateId)?.windowDays}
            defaultPreset={DEFAULT_PRESET_BY_TEMPLATE[templateId]}
            onApply={onConfirm}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
}
